import { CSSResultGroup, html, LitElement, nothing } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { style } from './carousel-pagination.style.js'
import { classMap } from 'lit/directives/class-map.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { fontStyles } from 'elements/_shared/fonts.style.js'

export const carouselPaginationVariants =
  ['default', 'simple', 'only-navigation', 'slider', 'nav-slider'] as const
export type carouselPaginationVariantTypes = typeof carouselPaginationVariants

@customElement('cse-carousel-pagination')
export class CSECarouselPagination extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style, fontStyles]

  @property({ type: String, reflect: true })
  variant: carouselPaginationVariantTypes[number] = 'default'

  @property({ type: Number, attribute: 'items-length' })
  itemsLength = 0

  @property({ type: Number, attribute: 'selected-item-index' })
  selectedItemIndex: number = 0

  @property({ type: Boolean })
  disabled = false

  @property({ type: Boolean })
  navigation = true

  @property({ type: Boolean })
  pagination = true

  @property({ type: String, attribute: 'previous-text' })
  previousText = ''

  @property({ type: String, attribute: 'next-text' })
  nextText = ''

  /** Whether the variant shows the rounded pill background wrapping the navigation arrows. */
  private get hasPillBackground(): boolean {
    if (this.variant === 'nav-slider') return false
    if (this.variant === 'default') return this.navigation
    return this.variant === 'only-navigation'
  }

  /** The icon-button variant used for the navigation arrows. */
  private get arrowVariant(): string {
    return this.variant === 'nav-slider' ? 'secondary' : 'primary'
  }

  private prevClick = () => {
    this.dispatchEvent(new prevClickEvent(this))
  }

  private nextClick = () => {
    this.dispatchEvent(new nextClickEvent(this))
  }

  private carouselItemClick = (index: number) => {
    this.dispatchEvent(new carouselItemClickEvent(this, index))
  }

  private dragging = false

  private get sliderTrack(): HTMLElement | null {
    return this.renderRoot.querySelector('.carousel-slider-track')
  }

  private clampIndex(index: number): number {
    return Math.min(this.itemsLength - 1, Math.max(0, index))
  }

  /** Dispatches a carousel-item-click only when the target position actually changes. */
  private goToSliderIndex(index: number) {
    const next = this.clampIndex(index)
    if (next !== this.selectedItemIndex) this.carouselItemClick(next)
  }

  private updateFromPointer(event: PointerEvent) {
    const track = this.sliderTrack
    if (!track || track.clientWidth === 0) return
    const rect = track.getBoundingClientRect()
    const ratio = (event.clientX - rect.left) / rect.width
    this.goToSliderIndex(Math.floor(ratio * this.itemsLength))
  }

  /**
   * Pointer capture keeps drag events flowing when the pointer leaves the track.
   * It is a progressive enhancement, so ignore an invalid pointer id (e.g. Firefox
   * throws NotFoundError for synthetic pointer events in tests).
   */
  private capturePointer(pointerId: number, capture: boolean) {
    const track = this.sliderTrack
    if (!track) return
    try {
      if (capture) track.setPointerCapture(pointerId)
      else track.releasePointerCapture(pointerId)
    } catch {
      /* pointer id is not active; capture is optional so ignore */
    }
  }

  private sliderPointerDown = (event: PointerEvent) => {
    if (this.disabled || this.itemsLength <= 0) return
    this.capturePointer(event.pointerId, true)
    this.dragging = true
    this.updateFromPointer(event)
  }

  private sliderPointerMove = (event: PointerEvent) => {
    if (!this.dragging) return
    this.updateFromPointer(event)
  }

  private sliderPointerUp = (event: PointerEvent) => {
    this.dragging = false
    this.capturePointer(event.pointerId, false)
  }

  private sliderKeyDown = (event: KeyboardEvent) => {
    if (this.disabled || this.itemsLength <= 0) return
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowUp':
        this.goToSliderIndex(this.selectedItemIndex + 1)
        break
      case 'ArrowLeft':
      case 'ArrowDown':
        this.goToSliderIndex(this.selectedItemIndex - 1)
        break
      case 'Home':
        this.goToSliderIndex(0)
        break
      case 'End':
        this.goToSliderIndex(this.itemsLength - 1)
        break
      default:
        return
    }
    event.preventDefault()
  }

  private renderPreviousButton() {
    return html`
      <cse-carousel-pagination-item
        class="carousel-pagination-previous"
        @click="${this.prevClick}"
        type="previous"
        variant="${this.arrowVariant}"
        ?disabled="${!(this.selectedItemIndex > 0) || this.disabled}"
      ></cse-carousel-pagination-item>
    `
  }

  private renderNextButton() {
    return html`
      <cse-carousel-pagination-item
        class="carousel-pagination-next"
        @click="${this.nextClick}"
        type="next"
        variant="${this.arrowVariant}"
        ?disabled="${!(this.selectedItemIndex < this.itemsLength - 1) || this.disabled}"
      >
      </cse-carousel-pagination-item>
    `
  }

  private renderPreviousNav() {
    return html`
      ${this.renderPreviousButton()}

      <div class="carousel-pagination-item carousel-pagination-item__previous">
        <cse-text>${this.previousText}</cse-text>
      </div>
    `
  }

  private renderNextNav() {
    return html`
      ${this.renderNextButton()}

      <div class="carousel-pagination-item carousel-pagination-item__next">
        <cse-text>${this.nextText}</cse-text>
      </div>
    `
  }

  private renderDots() {
    if (this.itemsLength <= 0) return nothing
    return Array.from({ length: this.itemsLength }).map((_, index: number) => html`
      <cse-carousel-pagination-item
        index="${index}"
        type="dot"
        ?selected="${index === this.selectedItemIndex}"
        @click="${() => this.carouselItemClick(index)}"
        ?disabled="${this.disabled}"
      ></cse-carousel-pagination-item>
    `)
  }

  private renderDefault() {
    return html`
      ${this.navigation ? this.renderPreviousNav() : nothing}
      ${this.pagination ? this.renderDots() : nothing}
      ${this.navigation ? this.renderNextNav() : nothing}
    `
  }

  private renderSimple() {
    return this.renderDots()
  }

  private renderSlider() {
    if (this.itemsLength <= 0) return nothing
    const fillFraction = (this.selectedItemIndex + 1) / this.itemsLength
    return html`
      <div
        class="carousel-slider-track"
        role="slider"
        tabindex="${this.disabled ? -1 : 0}"
        aria-valuemin="0"
        aria-valuemax="${this.itemsLength - 1}"
        aria-valuenow="${this.selectedItemIndex}"
        aria-disabled="${this.disabled}"
        @pointerdown="${this.sliderPointerDown}"
        @pointermove="${this.sliderPointerMove}"
        @pointerup="${this.sliderPointerUp}"
        @keydown="${this.sliderKeyDown}"
      >
        <div class="carousel-slider-fill" style="width: ${fillFraction * 100}%"></div>
      </div>
    `
  }

  private renderNavSlider() {
    return html`
      ${this.renderPreviousButton()}
      ${this.renderSlider()}
      ${this.renderNextButton()}
    `
  }

  private renderOnlyNavigation() {
    return html`
      ${this.renderPreviousNav()}
      ${this.renderNextNav()}
    `
  }

  private renderContent() {
    switch (this.variant) {
      case 'simple':
        return this.renderSimple()
      case 'slider':
        return this.renderSlider()
      case 'nav-slider':
        return this.renderNavSlider()
      case 'only-navigation':
        return this.renderOnlyNavigation()
      default:
        return this.renderDefault()
    }
  }

  override render() {
    return html`
      <div class="${classMap({
        'carousel-controls': true,
        [`variant-${this.variant}`]: true,
        'navigation': this.hasPillBackground,
        'disabled': this.disabled
        })}">
        ${this.renderContent()}
      </div>
    `
  }
}

export class prevClickEvent extends Event {
  static type = 'prev-click' as const

  carouselPagination: CSECarouselPagination

  constructor(carouselPagination: CSECarouselPagination) {
    super(prevClickEvent.type, { bubbles: true, composed: true })
    this.carouselPagination = carouselPagination
  }
}

export class nextClickEvent extends Event {
  static type = 'next-click' as const

  carouselPagination: CSECarouselPagination

  constructor(carouselPagination: CSECarouselPagination) {
    super(nextClickEvent.type, { bubbles: true, composed: true })
    this.carouselPagination = carouselPagination
  }
}

export class carouselItemClickEvent extends Event {
  static type = 'carousel-item-click' as const

  carouselPagination: CSECarouselPagination
  index: number

  constructor(carouselPagination: CSECarouselPagination, index: number) {
    super(carouselItemClickEvent.type, { bubbles: true, composed: true })
    this.carouselPagination = carouselPagination
    this.index = index
  }
}
